import type React from 'react';

import { SocialSharing, SocialSharingGroup } from '@change/ds-core-ui';
import type { Icon } from '@change/ds-icons';
import { EmailIcon, FacebookIcon, TwitterIcon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';

import { useShareLinks } from './hooks/useShareLinks';
import type { Channel } from './hooks/useShareLinks';

export const ShareLinks: React.FC = () => {
	const {
		data: { shareActions },
	} = useShareLinks();

	return (
		<SocialSharingGroup layout="inline" className="gap-4">
			{shareActions.map(({ channel, ShareAction }) => (
				<ShareAction key={`article-share-${channel}`}>
					<ShareIcon channel={channel} />
				</ShareAction>
			))}
		</SocialSharingGroup>
	);
};

const socialIcons: Readonly<
	Record<
		Channel,
		Readonly<{
			icon: React.FC<Icon>;
			displayLabel: TranslationKey;
		}>
	>
> = {
	twitter: {
		icon: TwitterIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.twitter.button_label'),
	},
	facebook: {
		icon: FacebookIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.facebook.button_label'),
	},
	email: {
		icon: EmailIcon,
		displayLabel: translationKey('corgi.petition_guides_article.page_heading.share.email.button_label'),
	},
} as const;

const ShareIcon: React.FC<{ channel: Channel; onClick?: () => void }> = ({ channel, onClick }) => {
	const { translate } = useI18n();
	const { icon: IconComponent, displayLabel } = socialIcons[channel];
	const variant = channel === 'email' ? 'default' : channel;
	return (
		<SocialSharing
			data-testid={`article-share-${channel}`}
			data-qa={`article-share-${channel}`}
			aria-label={translate(displayLabel)}
			onClick={onClick}
			className="w-12"
			icon={<IconComponent />}
			label=""
			size="large"
			variant={variant}
		/>
	);
};
